import * as React from "react";

/** Faixa reservada aos sheets; diálogos globais ficam em GLOBAL_DIALOG_* (abaixo). */
export const SHEET_Z_MIN = 200;
export const SHEET_Z_MAX = 8990;
export const SHEET_Z_STEP = 2;

/** z-index do menu dropdown / select acima do sheet mais alto aberto. */
export const SHEET_STACK_MENU_Z_BOOST = 40;
export const SHEET_STACK_SELECT_Z_BOOST = 40;

export const GLOBAL_DIALOG_BACKDROP_Z = 9999;
export const GLOBAL_DIALOG_CONTENT_Z = 10000;

export interface SheetStackContextValue {
  maxOpenZ: number;
  allocate: () => number;
  release: (z: number) => void;
}

export const SheetStackContext = React.createContext<SheetStackContextValue | null>(null);

export function SheetStackProvider({ children }: { children: React.ReactNode }) {
  const activeZsRef = React.useRef(new Set<number>());
  const nextZRef = React.useRef(SHEET_Z_MIN);

  const recomputeMax = React.useCallback(() => {
    let m = 0;
    activeZsRef.current.forEach((z) => {
      if (z > m) m = z;
    });
    return m;
  }, []);

  const [maxOpenZ, setMaxOpenZ] = React.useState(0);

  const allocate = React.useCallback(() => {
    const active = activeZsRef.current;
    const span = Math.max(1, Math.floor((SHEET_Z_MAX - SHEET_Z_MIN) / SHEET_Z_STEP));
    const maxAttempts = span + 5;
    let attempts = 0;
    let z: number;

    do {
      z = nextZRef.current;
      nextZRef.current += SHEET_Z_STEP;
      if (nextZRef.current > SHEET_Z_MAX) {
        nextZRef.current = SHEET_Z_MIN;
      }
      attempts++;
      if (attempts > maxAttempts) {
        z = SHEET_Z_MIN;
        for (let probe = SHEET_Z_MIN; probe <= SHEET_Z_MAX; probe += SHEET_Z_STEP) {
          if (!active.has(probe)) {
            z = probe;
            break;
          }
        }
        break;
      }
    } while (active.has(z));

    active.add(z);
    setMaxOpenZ((prev) => (z > prev ? z : prev));
    return z;
  }, []);

  const release = React.useCallback(
    (released: number) => {
      activeZsRef.current.delete(released);
      setMaxOpenZ(recomputeMax());
    },
    [recomputeMax],
  );

  const value = React.useMemo(
    () => ({
      maxOpenZ,
      allocate,
      release,
    }),
    [maxOpenZ, allocate, release],
  );

  return <SheetStackContext.Provider value={value}>{children}</SheetStackContext.Provider>;
}

export function useOptionalSheetStack(): SheetStackContextValue | null {
  return React.useContext(SheetStackContext);
}

/** Maior z-index entre sheets ainda montados; 0 fora do provider ou sem sheets. */
export function useSheetStackMaxOpenZ(): number {
  return React.useContext(SheetStackContext)?.maxOpenZ ?? 0;
}
